import React, { ComponentType } from "react";
import { IconProps } from "@/components/icons/types";
import { PlusIcon } from "@/components/icons/PlusIcon";
import { SendIcon } from "@/components/icons/SendIcon";
import { CollectionIcon } from "@/components/icons/CollectionIcon";
import { PostIcon } from "@/components/icons/PostIcon";
import { LightbulbIcon } from "@/components/icons/LightbulbIcon";
import { LeadIcon } from "@/components/icons/LeadIcon";
import { ClientIcon } from "@/components/icons/ClientIcon";
import { NoteIcon } from "@/components/icons/NoteIcon";
import { LibraryIcon } from "@/components/icons/LibraryIcon";
import { BriefIcon } from "@/components/icons/BriefIcon";
import { HomeIcon } from "@/components/icons/HomeIcon";
import { GridIcon } from "@/components/icons/GridIcon";
import { ChartBarIcon } from "@/components/icons/ChartBarIcon";
import { BoltIcon } from "@/components/icons/BoltIcon";
import { CogIcon } from "@/components/icons/CogIcon";
import { TaskIcon } from "@/components/icons/TaskIcon";
import { CalendarCheckIcon } from "@/components/icons/CalendarCheckIcon";
import { CheckIcon } from "@/components/icons/CheckIcon";
import { WalletIcon } from "@/components/icons/WalletIcon";
import { FolderIcon } from "@/components/icons/FolderIcon";
import { DocumentIcon } from "@/components/icons/DocumentIcon";
import { UsersIcon } from "@/components/icons/UsersIcon";
import { ChevronDownIcon } from "@/components/icons/ChevronDownIcon";
import { Button } from "@/components/ui/Button";
import { DropdownMenu } from "@/components/ui/DropdownMenu";
import { cn } from "@/utils/cn";

interface EntityDef {
  icon: ComponentType<IconProps>;
  label: string;
  plural: string;
  createLabel: string;
}

export const ENTITY_CONFIG = {
  collection: {
    icon: CollectionIcon,
    label: "Collection",
    plural: "Collections",
    createLabel: "New Collection",
  },
  story: {
    icon: PostIcon,
    label: "Story",
    plural: "Stories",
    createLabel: "New Story",
  },
  post: {
    icon: PostIcon,
    label: "Post",
    plural: "Posts",
    createLabel: "New Post",
  },
  idea: {
    icon: LightbulbIcon,
    label: "Idea",
    plural: "Ideas",
    createLabel: "New Idea",
  },
  lead: {
    icon: LeadIcon,
    label: "Lead",
    plural: "Leads",
    createLabel: "New Lead",
  },
  client: {
    icon: ClientIcon,
    label: "Client",
    plural: "Clients",
    createLabel: "New Client",
  },
  note: {
    icon: NoteIcon,
    label: "Note",
    plural: "Notes",
    createLabel: "New Note",
  },
  media: {
    icon: LibraryIcon,
    label: "Media",
    plural: "Library",
    createLabel: "New Upload",
  },
  brief: {
    icon: BriefIcon,
    label: "Brief",
    plural: "Briefs",
    createLabel: "New Brief",
  },
  calendarEvent: {
    icon: CalendarCheckIcon,
    label: "Event",
    plural: "Events",
    createLabel: "New Event",
  },
  home: {
    icon: HomeIcon,
    label: "Home",
    plural: "Home",
    createLabel: "",
  },
  grid: {
    icon: GridIcon,
    label: "Grid",
    plural: "Grid",
    createLabel: "",
  },
  ads: {
    icon: BoltIcon,
    label: "Ads",
    plural: "Ads",
    createLabel: "",
  },
  finance: {
    icon: WalletIcon,
    label: "Finance",
    plural: "Finance",
    createLabel: "",
  },
  expense: {
    icon: WalletIcon,
    label: "Expense",
    plural: "Expenses",
    createLabel: "Add expense",
  },
  // A job is one line item (the user, 2026-10-21): a name, its own rhythm, a price, a margin % and who is paid its cost.
  // It starts directly with New job, or Active from a quote (finance-real "A job is a line item"). The contract record
  // leaves the screen, so this key, which main's agency screens read as "No contract", now names the job: they read
  // "No job". The key keeps its name so every call site follows without an edit.
  contract: {
    icon: FolderIcon,
    label: "Job",
    plural: "Jobs",
    createLabel: "New job",
  },
  // Main's per-period record (TD-108's job): one month, run of posts or one-off of a job. Its period is the month, so it
  // reads Month, and the month drawer, Send and Mark paid keep working on it. Done months make an invoice.
  job: {
    icon: CheckIcon,
    label: "Month",
    plural: "Months",
    createLabel: "New month",
  },
  quote: {
    icon: DocumentIcon,
    label: "Quote",
    plural: "Quotes",
    createLabel: "New quote",
  },
  // A client invoice made by hand (slice 5, J5): a month from a day, or a run of picked posts.
  invoice: {
    icon: SendIcon,
    label: "Invoice",
    plural: "Invoices",
    createLabel: "New invoice",
  },
  // One person's invoice to the agency (finance-real, the user 2026-10-22): their claims on jobs, paid out on Paid.
  teamInvoice: {
    icon: DocumentIcon,
    label: "Team invoice",
    plural: "Team invoices",
    createLabel: "New team invoice",
  },
  // What one person collects from one job (finance-real, the user 2026-10-22): a table of its own, within the job's cap.
  claim: {
    icon: WalletIcon,
    label: "Claim",
    plural: "Claims",
    createLabel: "Add a claim",
  },
  // Someone on the Team tab, paid from their claims: the person drawer's name while it loads.
  person: {
    icon: UsersIcon,
    label: "Person",
    plural: "People",
    createLabel: "",
  },
  // Someone the agency pays who is not on the team and never signs in (TD-106), added from the Team tab.
  freelancer: {
    icon: UsersIcon,
    label: "Freelancer",
    plural: "Freelancers",
    createLabel: "Add freelancer",
  },
  // A line added once to a running contract (TD-099), billed with its next invoice.
  oneOff: {
    icon: BoltIcon,
    label: "One-off",
    plural: "One-offs",
    createLabel: "Add one-off",
  },
  // The website's "from" prices, one version at a time (TD-083).
  listino: {
    icon: CollectionIcon,
    label: "Listino",
    plural: "Listini",
    createLabel: "New listino",
  },
  // The catalogs a price splits over (TD-075): what is sold, and the work that makes it.
  serviceLine: {
    icon: GridIcon,
    label: "Service line",
    plural: "Service lines",
    createLabel: "Add service line",
  },
  workType: {
    icon: TaskIcon,
    label: "Work type",
    plural: "Work types",
    createLabel: "Add work type",
  },
  expenseCategory: {
    icon: WalletIcon,
    label: "Category",
    plural: "Categories",
    createLabel: "Add category",
  },
  reporting: {
    icon: ChartBarIcon,
    label: "Reporting",
    plural: "Reporting",
    createLabel: "",
  },
  setup: {
    icon: CogIcon,
    label: "Setup",
    plural: "Setup",
    createLabel: "",
  },
  task: {
    icon: TaskIcon,
    label: "Task",
    plural: "Tasks",
    createLabel: "New Task",
  },
} as const satisfies Record<string, EntityDef>;

export type EntityKey = keyof typeof ENTITY_CONFIG;

interface EntityCreateButtonProps {
  entity: EntityKey;
  onClick: () => void;
  /** Placement only (`w-full`, `mt-4`): the look is the Button's (J25). */
  className?: string;
  iconSize?: string;
  /** The Button's emphasis: primary, or secondary beside a stronger action or in a list (DD-056). */
  variant?: "primary" | "secondary";
  /** Small by default, the size of the header buttons beside it (DD-056). */
  size?: "sm" | "md";
}

/** Every create button is the shared Button, so it wears the brand gradient like the buttons beside it (J13, J25). */
export function EntityCreateButton({ entity, onClick, className, iconSize = "w-4 h-4", variant = "primary", size = "sm" }: EntityCreateButtonProps) {
  const config = ENTITY_CONFIG[entity];
  return (
    <Button variant={variant} size={size} onClick={onClick} className={cn("gap-1.5", className)}>
      <PlusIcon className={iconSize} />
      {config.createLabel}
    </Button>
  );
}

interface EntityCreateMenuProps {
  /** The button's own label: "New invoice". */
  label: string;
  /** The kinds it makes, each its entity's createLabel, with a tag saying which way it goes ("To client"). */
  items: { entity: EntityKey; badge?: string; onClick: () => void }[];
}

/** A create button for two kinds or more (DD-056): EntityCreateButton's look, a chevron, and a menu of each kind's
 * createLabel. The menu opens under the button, in the page, not in a portal, so a scroll never closes it as it opens. */
export function EntityCreateMenu({ label, items }: EntityCreateMenuProps) {
  return (
    <DropdownMenu
      align="right"
      width="w-60"
      menuClassName="rounded-xl"
      items={items.map((i) => ({ id: i.entity, label: ENTITY_CONFIG[i.entity].createLabel, badge: i.badge, onClick: i.onClick }))}
      trigger={
        <Button variant="primary" size="sm" className="gap-1.5" aria-haspopup="menu">
          <PlusIcon className="w-4 h-4" />
          {label}
          <ChevronDownIcon className="w-3.5 h-3.5 opacity-80" />
        </Button>
      }
    />
  );
}
